/**
 * The plan's turn handle, the app's RotateMarkers for the whole plan (LevelController, PlanRotationAnchor): a round
 * button on a line from the building's middle, past the floor's longest outer wall. Dragged round, every floor turns
 * about that middle (`rotateScene`), held on a right angle within 3° unless ⌘ or Ctrl is down, its bearing beside it,
 * one undo step a drag; Escape lets a drag go. Focused, the arrows turn the plan by 1°, by 15° with Shift, a run of
 * them one undo step.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import type { Scene, Vec2 } from '../core/hp/types';
import { bearingDegrees, heldTurn, planRotationAnchor, planRotationCentre, rotateScene } from '../state/planRotation';
import { store } from '../state/store';
import { cursorStyle, rotateGlyphAngle } from '../render/three/cursors';
import { Icon } from './Icon';
import { useStore } from './useStore';
import { t } from '../i18n/I18n';

type PlanView = { toScreen(point: Vec2): { x: number; y: number }; toPlan(x: number, y: number): Vec2 };
const planView = () => {
  const canvas = document.querySelector<HTMLCanvasElement>('canvas.plan-canvas');
  const view = (canvas as unknown as { __planView?: PlanView } | null)?.__planView;
  return canvas && view ? { canvas, view } : null;
};
/** The handle stands this far past the outer wall's face, in screen pixels. */
const STANDOFF = 46;

export function PlanRotationHandle() {
  const scene = useStore(s => s.scene), levelIndex = useStore(s => s.activeLevelIndex);
  const level = scene?.levels[levelIndex];
  // The middle a drag turns about is taken when it starts and kept: a turn keeps it where it is.
  const drag = useRef<{ pointer: number; base: Scene; centre: Vec2; start: number; bearing: number } | null>(null);
  const centre = useMemo(() => scene ? planRotationCentre(scene) : null, [scene]);
  // The wall the handle stood off last: it keeps it while that wall is still an outer one.
  const kept = useRef<string | undefined>(undefined);
  const anchor = useMemo(() => level && centre ? planRotationAnchor(level, centre, kept.current) : null, [level, centre]);
  kept.current = anchor?.wall.id;
  const [place, setPlace] = useState<{ cx: number; cy: number; hx: number; hy: number } | null>(null);
  const [turning, setTurning] = useState<number | null>(null);
  // The plan pans and zooms under it: its place follows the view frame by frame.
  useLayoutEffect(() => {
    let frame = 0, previous = '';
    const follow = () => {
      frame = requestAnimationFrame(follow);
      const plan = planView(), middle = drag.current?.centre ?? centre;
      if (!plan || !middle || !anchor) { if (previous) { previous = ''; setPlace(null); } return; }
      const rect = plan.canvas.getBoundingClientRect(), c = plan.view.toScreen(middle);
      const face = plan.view.toScreen({ x: middle.x + anchor.outward.x * anchor.reach, z: middle.z + anchor.outward.z * anchor.reach });
      const dx = face.x - c.x, dy = face.y - c.y, length = Math.hypot(dx, dy) || 1;
      // Kept on the canvas, clear of its edges: the turn is measured from where the press took it, not from the ray.
      const hx = Math.min(Math.max(rect.left + face.x + dx / length * STANDOFF, rect.left + 32), rect.right - 32);
      const hy = Math.min(Math.max(rect.top + face.y + dy / length * STANDOFF, rect.top + 32), rect.bottom - 32);
      const next = { cx: rect.left + c.x, cy: rect.top + c.y, hx, hy };
      const key = JSON.stringify([Math.round(next.cx), Math.round(next.cy), Math.round(next.hx), Math.round(next.hy)]);
      if (key !== previous) { previous = key; setPlace(next); }
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [centre, anchor]);
  // Escape lets a drag go before it ends the mode.
  useEffect(() => {
    if (turning === null) return;
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !drag.current) return;
      event.preventDefault(); event.stopPropagation();
      drag.current = null; setTurning(null); store.cancelStroke();
    };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, [turning]);

  const angleAt = (event: ReactPointerEvent, middle: Vec2) => {
    const plan = planView();
    if (!plan) return null;
    const rect = plan.canvas.getBoundingClientRect(), point = plan.view.toPlan(event.clientX - rect.left, event.clientY - rect.top);
    return Math.atan2(point.z - middle.z, point.x - middle.x);
  };
  const release = (event: ReactPointerEvent, done: boolean) => {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null; setTurning(null);
    if (done) store.endStroke(); else store.cancelStroke();
  };
  const keys = (event: ReactKeyboardEvent) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step || !centre || drag.current) return;
    event.preventDefault(); event.stopPropagation();
    const turn = step * (event.shiftKey ? 15 : 1) * Math.PI / 180;
    store.commit('plan-rotation-keys', current => rotateScene(current, turn, centre));
  };
  if (!place || !anchor || !centre) return null;
  // The bearing is the turned wall's own: it is read from the plan as it now stands.
  const bearing = bearingDegrees(anchor.bearing);
  return <>
    <svg className="plan-rotation-line" aria-hidden="true"><line x1={place.cx} y1={place.cy} x2={place.hx} y2={place.hy} /><circle cx={place.cx} cy={place.cy} r="4" /></svg>
    <button type="button" className="plan-rotation-handle" data-active={turning !== null || undefined}
      style={{ left: place.hx, top: place.hy, cursor: cursorStyle('rotate', rotateGlyphAngle(place.cx, place.cy, place.hx, place.hy)) }}
      aria-label={t('#EditorRotatePlanHandle', bearing)} title={t('#EditorRotatePlanHandleTooltip')}
      onPointerDown={event => {
        if (event.button !== 0 || drag.current) return;
        event.preventDefault(); event.stopPropagation();
        const base = store.get().scene, start = angleAt(event, centre);
        if (!base || start === null) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { pointer: event.pointerId, base, centre, start, bearing: anchor.bearing };
        store.beginGeometryStroke();
        setTurning(0);
      }}
      onPointerMove={event => {
        const held = drag.current;
        if (!held || held.pointer !== event.pointerId) return;
        const angle = angleAt(event, held.centre);
        if (angle === null) return;
        let turn = angle - held.start;
        turn = Math.atan2(Math.sin(turn), Math.cos(turn));
        turn = heldTurn(held.bearing, turn, event.ctrlKey || event.metaKey);
        setTurning(turn);
        store.commit('', () => rotateScene(held.base, turn, held.centre));
      }}
      onPointerUp={event => release(event, true)} onPointerCancel={event => release(event, false)} onLostPointerCapture={event => release(event, true)}
      onKeyDown={keys}>
      <Icon name="rotate" size={20} />
    </button>
    {turning !== null && <span className="plan-rotation-readout" role="status" style={{ left: place.hx, top: place.hy - 40 }}>{bearing}°</span>}
  </>;
}
